import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Card, CardHeader, CardBody, Button, StatusBadge, Loading, ErrorNotice,
  Table, THead, TBody, TR, TH, TD, ConfirmDialog, EmptyState,
} from '../../components/ui/index.js';
import { useResource, useMutation } from '../../hooks/useApi.js';
import { useToast } from '../../components/ToastProvider.jsx';
import { http } from '../../lib/api.js';
import { formatMoney, formatNumber, formatTimestamp, formatExactTimestamp } from '../../lib/format.js';
import { PageHero } from '../../components/PageHero.jsx';

export function AdminMerchantDetailPage() {
  const { id } = useParams();
  const toast = useToast();
  const { data, error, loading, refetch } = useResource(`/admin/merchants/${id}`);
  const [confirming, setConfirming] = useState(null);

  const setStatus = useMutation(async (suspend) => {
    await http.post(`/admin/merchants/${id}/${suspend ? 'suspend' : 'reactivate'}`);
    toast.success(suspend ? 'Merchant suspended' : 'Merchant reactivated');
    setConfirming(null);
    refetch();
  });

  if (loading) return <Loading label="Loading merchant…" />;
  if (error) {
    return (
      <>
        <PageHero title="Merchant" breadcrumb={<Link to="/admin/merchants" className="hover:underline">← Merchants</Link>} />
        <ErrorNotice error={error} onRetry={refetch} />
      </>
    );
  }

  const suspended = data.status === 'suspended';

  return (
    <>
      <PageHero
        backTo="/admin/merchants"
        title={data.name}
        blurb={data.email}
        actions={
          <>
            <StatusBadge status={data.status} />
            <Button variant={suspended ? 'primary' : 'danger'} onClick={() => setConfirming(suspended ? 'reactivate' : 'suspend')}>
              {suspended ? 'Reactivate' : 'Suspend'}
            </Button>
          </>
        }
      />

      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <Card><CardBody>
          <p className="text-xs uppercase tracking-wide text-muted">Transactions</p>
          <p className="tabular mt-1 text-2xl font-semibold text-ink">{formatNumber(data.transactionCount)}</p>
        </CardBody></Card>
        <Card><CardBody>
          <p className="text-xs uppercase tracking-wide text-muted">Volume</p>
          {/* An organisation can take more than one currency; show each as itself. */}
          {(data.volumeByCurrency ?? []).length === 0 ? (
            <p className="tabular mt-1 text-2xl font-semibold text-ink">—</p>
          ) : (
            data.volumeByCurrency.map((v) => (
              <p key={v.currency} className="tabular mt-1 text-2xl font-semibold text-ink">
                {formatMoney(v.volume, v.currency)}
              </p>
            ))
          )}
        </CardBody></Card>
        <Card><CardBody>
          <p className="text-xs uppercase tracking-wide text-muted">Joined</p>
          <p className="mt-1 text-sm text-ink">{formatExactTimestamp(data.createdAt)}</p>
        </CardBody></Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title="Connected providers" />
          <CardBody>
            {data.connections?.length ? (
              <ul className="space-y-3">
                {data.connections.map((connection) => (
                  <li key={connection.provider} className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium capitalize text-ink">{connection.provider}</p>
                      <p className="text-xs text-muted">
                        {/* AD-6: the same masked hints a merchant sees, and nothing more.
                            This page never loads the encrypted credentials at all. */}
                        {Object.entries(connection.credentialHints ?? {}).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(' · ') || 'No hints'}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-1.5">
                      <StatusBadge status={connection.environment} size="sm" />
                      <StatusBadge status={connection.status} size="sm" />
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted">No providers connected.</p>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Recent payments"
            actions={
              <Link to={`/admin/payments?merchantId=${encodeURIComponent(data.id)}`} className="text-xs font-medium text-accent-ink hover:underline">
                View all
              </Link>
            }
          />
          {data.recentPayments?.length ? (
            <Table className="min-w-0">
              <THead><TR><TH>Payment</TH><TH align="right">Amount</TH><TH>Status</TH></TR></THead>
              <TBody>
                {data.recentPayments.map((payment) => (
                  <TR key={payment.id}>
                    <TD>
                      <Link to={`/admin/payments/${payment.id}`} className="font-mono text-xs text-accent-ink hover:underline">
                        {payment.id}
                      </Link>
                    </TD>
                    <TD align="right" className="tabular">{formatMoney(payment.amount, payment.currency)}</TD>
                    <TD><StatusBadge status={payment.status} size="sm" /></TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          ) : (
            <EmptyState title="No payments" blurb="This merchant has not taken a payment yet." />
          )}
        </Card>

        {/* AD-2: an audit trail. AD-7: every admin action lands here. */}
        <Card className="lg:col-span-2">
          <CardHeader title="Audit trail" blurb="Security and money events for this account" />
          {data.auditTrail?.length ? (
            <Table>
              <THead><TR><TH>Action</TH><TH>Actor</TH><TH>IP</TH><TH align="right">When</TH></TR></THead>
              <TBody>
                {data.auditTrail.map((entry) => (
                  <TR key={entry.id}>
                    <TD className="font-mono text-xs">{entry.action}</TD>
                    <TD className="text-xs text-ink-soft">{entry.actorType}{entry.actorId ? ` · ${entry.actorId}` : ''}</TD>
                    <TD className="font-mono text-xs text-muted">{entry.ip ?? '—'}</TD>
                    <TD align="right" className="text-xs text-muted">{formatTimestamp(entry.createdAt)}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          ) : (
            <EmptyState title="No audit entries" blurb="Logins, key changes, refunds and admin actions appear here." />
          )}
        </Card>
      </div>

      <ConfirmDialog
        open={Boolean(confirming)}
        onClose={() => setConfirming(null)}
        onConfirm={() => setStatus.mutate(confirming === 'suspend').catch(() => {})}
        loading={setStatus.loading}
        variant={confirming === 'suspend' ? 'danger' : 'primary'}
        title={confirming === 'suspend' ? `Suspend ${data.name}?` : `Reactivate ${data.name}?`}
        consequence={
          confirming === 'suspend'
            ? 'They will be unable to create new payments immediately, including any checkout a customer has open right now. Existing payments and refunds are unaffected. This is written to the audit log.'
            : 'They will be able to create payments again. If they have no provider connected they return to onboarding rather than active. This is written to the audit log.'
        }
        confirmLabel={confirming === 'suspend' ? 'Suspend merchant' : 'Reactivate merchant'}
      />
    </>
  );
}

export default AdminMerchantDetailPage;
